import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';

interface AnimatedCounterProps {
  value: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  className?: string;
}

const CounterContainer = styled.div`
  display: inline-block;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
`;

const CounterNumber = styled.span`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary[600]};
  line-height: 1;
  transition: all ${theme.transitions.base};
`;

const CounterLabel = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  margin-left: ${theme.spacing[1]};
`;

const useCountAnimation = (
  targetValue: number,
  duration: number = 2000,
  decimals: number = 0
) => {
  const [currentValue, setCurrentValue] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (targetValue === 0) {
      setCurrentValue(0);
      return;
    }

    setIsAnimating(true);
    const startTime = Date.now();
    const startValue = currentValue;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      
      const newValue = startValue + (targetValue - startValue) * easeOutQuart;
      setCurrentValue(newValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCurrentValue(targetValue);
        setIsAnimating(false);
      }
    };

    requestAnimationFrame(animate);
  }, [targetValue, duration]);

  return {
    displayValue: currentValue.toFixed(decimals),
    isAnimating
  };
};

export const AnimatedCounter: React.FC<AnimatedCounterProps> = ({
  value,
  duration = 2000,
  prefix = '',
  suffix = '',
  decimals = 0,
  className
}) => {
  const { displayValue, isAnimating } = useCountAnimation(value, duration, decimals);

  return (
    <CounterContainer className={className}>
      <CounterNumber>
        {prefix}{displayValue}{suffix}
      </CounterNumber>
    </CounterContainer>
  );
};

// Preset counter components for common use cases
export const CurrencyCounter: React.FC<Omit<AnimatedCounterProps, 'prefix' | 'decimals'>> = (props) => (
  <AnimatedCounter {...props} prefix="$" decimals={2} />
);

export const PercentageCounter: React.FC<Omit<AnimatedCounterProps, 'suffix' | 'decimals'>> = (props) => (
  <AnimatedCounter {...props} suffix="%" decimals={1} />
);

export const NumberCounter: React.FC<AnimatedCounterProps> = (props) => (
  <AnimatedCounter {...props} decimals={0} />
);